/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { WiresService, WireUploadFormat } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-actions-column';
import '@treasury/presentation/components/tm-icon';
import '@vaadin/grid';
// import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { TmDetailedError } from '@treasury/domain/shared';
import '@treasury/presentation/components/tm-list-header';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { columnBodyRenderer } from '@vaadin/grid/lit';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';

export const tagName = 'wire-upload-formats-table';

@customElement(tagName)
export class WireUploadFormatsTable extends TmContainer {
    @property()
    public wireFileFormats: WireUploadFormat[] = [];

    @InjectProperty()
    declare private readonly wireService: WiresService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private filteredWireFileFormats: WireUploadFormat[] = [];

    @state()
    private searchTerm: string = '';

    @state()
    private fieldsToFilter = ['formatName', 'formatType', 'updatedBy', 'updatedDateFormatted'];

    @state()
    private filteredWireFileFormatsCount: number = 0;

    @state()
    private currentPage: number = 1;

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div class="flex-none">
                <tm-list-header
                    .filterButton=${false}
                    .searchTerm=${this.searchTerm}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-wire-formats'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                ></tm-list-header>
            </div>
            <div class="flex-grow">
                <vaadin-grid
                    class=${classMap({
                        'h-full': true,
                        'shadow-md': !this.isDesktop,
                    })}
                    .items=${this.filteredWireFileFormats}
                    @active-item-changed=${({ detail }: CustomEvent) => {
                        if (!detail.value) {
                            return;
                        }
                        this.dispatchEvent(
                            new CustomEvent('wire-file-selected', {
                                detail: {
                                    wireFile: detail.value,
                                },
                                bubbles: true,
                                composed: true,
                            })
                        );
                    }}
                    ><vaadin-grid-sort-column
                        header="Format"
                        path="formatName"
                    ></vaadin-grid-sort-column
                    ><vaadin-grid-sort-column
                        header="Type"
                        path="formatType"
                    ></vaadin-grid-sort-column
                    ><vaadin-grid-sort-column
                        header="Created Date"
                        path="updatedDateFormatted"
                    ></vaadin-grid-sort-column
                    ><vaadin-grid-sort-column
                        header="Created By"
                        path="updatedBy"
                    ></vaadin-grid-sort-column
                    ><vaadin-grid-column
                        frozen-to-end
                        text-align="end"
                        flex-grow="0"
                        auto-width
                        @click=${(e: Event) => e.stopPropagation()}
                        ${columnBodyRenderer((format: WireUploadFormat) => {
                            const actions = [{ text: 'Delete' }];
                            return html`<tm-actions-column
                                .items=${actions}
                                @action-selected=${(e: CustomEvent) => {
                                    const action = e.detail.value.text;
                                    if (action === 'Delete') {
                                        this.deleteWireUploadFormat(format);
                                    }
                                }}
                            ></tm-actions-column>`;
                        })}
                    ></vaadin-grid-column
                ></vaadin-grid>
            </div>
            <div class="flex-none">${this.renderPaginationControls()}</div>
        </div>`;
    }

    protected renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredWireFileFormatsCount}
            .currentPage=${this.currentPage}
            .pageSize=${25}
            .itemLabel=${'upload formats'}
            @page-changed=${(e: CustomEvent) => {
                this.currentPage = e.detail.currentPage;
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    protected updated(_changedProperties: Map<string | number | symbol, unknown>) {
        if (_changedProperties.has('wireFileFormats')) {
            this.filteredWireFileFormats = this.wireFileFormats;
            this.currentPage = 1;
            this.searchTerm = '';
            this.filterResults({ detail: { value: this.searchTerm } } as CustomEvent);
        }
    }

    private filterResults(e?: CustomEvent) {
        let filteredWireUploadFormats = clone(this.wireFileFormats);
        if (e) {
            if (typeof e === 'string') {
                this.searchTerm = '';
            } else {
                this.searchTerm = e.detail.value;
            }
        }
        filteredWireUploadFormats = filterTableResults(
            { detail: { value: this.searchTerm } } as CustomEvent,
            this.wireFileFormats,
            this.fieldsToFilter
        ) as [];
        this.filteredWireFileFormatsCount = filteredWireUploadFormats.length;
        const offset = this.paginationControls?.calculateOffset();
        const limit = this.paginationControls?.pageSize;
        this.filteredWireFileFormats = filteredWireUploadFormats.slice(offset, offset + limit);
    }

    private async deleteWireUploadFormat(format: WireUploadFormat) {
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to delete ${format.formatName}?</div>`,
            'Delete Wire Upload Format',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Close',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                },
            }
        );
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
        if (reason === DynamicComponentExitReason.Confirm) {
            try {
                await this.wireService.deleteWireUploadFormat(format.id);
                this.dispatchEvent(
                    new CustomEvent('wire-upload-format-deleted', {
                        detail: {
                            wireUploadFormat: format,
                        },
                    })
                );
                this.notificationService.renderSuccess('Wire upload format deleted successfully.');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error decisioning wire payment.');
                }
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
                vaadin-grid::part(cell) {
                    align-items: center;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireUploadFormatsTable;
    }
}
